import { useCallback, useEffect, useState } from "react";
import { Building2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { SizeBadge, DemoBadge } from "@/components/badges";
import SitesSection from "@/components/company/SitesSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EMPTY_FORM = {
  denominazione: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "",
  forma_giuridica: "", indirizzo: "", citta: "", provincia: "", cap: "",
  nazione: "Italia", codice_ateco: "", codice_nace: "", settore: "",
  numero_dipendenti: "", fatturato: "", totale_attivo: "", sito_web: "",
  email: "", appartenenza_gruppo: false, descrizione_attivita: "",
};

function Field({ id, label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value ?? ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function CompanyProfile() {
  const { companyId, can, role, refreshCompany } = useTenant();
  const [form, setForm] = useState(EMPTY_FORM);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  const canEdit = can("company:update");

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data: c } = await api.get(`/companies/${companyId}`);
      setCompany(c);
      setForm({
        ...EMPTY_FORM,
        ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, c[k] ?? EMPTY_FORM[k]])),
        numero_dipendenti: c.numero_dipendenti ?? "",
        fatturato: c.fatturato ?? "",
        totale_attivo: c.totale_attivo ?? "",
      });
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        numero_dipendenti: form.numero_dipendenti === "" ? null : parseInt(form.numero_dipendenti, 10),
        fatturato: form.fatturato === "" ? null : parseFloat(form.fatturato),
        totale_attivo: form.totale_attivo === "" ? null : parseFloat(form.totale_attivo),
      };
      const { data } = await api.put(`/companies/${companyId}`, payload);
      setCompany(data);
      refreshCompany?.();
      toast.success("Anagrafica aggiornata", { description: data.denominazione });
    } catch (err) {
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="company-profile-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-green-700" aria-hidden="true" />
            Anagrafica Azienda
          </h1>
          <p className="text-sm text-slate-600 mt-1">Dati identificativi, sede legale e classificazione dimensionale dell'impresa.</p>
        </div>
        <div className="flex items-center gap-2">
          <SizeBadge size={company?.dimensione_pmi} />
          {company?.is_demo && <DemoBadge />}
        </div>
      </div>

      {company?.dimensione_preliminare && company?.dimensione_pmi && (
        <p className="text-xs text-slate-500" data-testid="classification-note">
          Classificazione preliminare da verificare. Basata su dipendenti, fatturato e totale attivo (Raccomandazione UE 2003/361/CE): non considera ancora imprese collegate, associate e partecipate.
        </p>
      )}

      <form onSubmit={save} className="space-y-6">
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Dati identificativi</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field id="denominazione" label="Denominazione *" value={form.denominazione} onChange={set("denominazione")} testid="company-denominazione-input" />
            <Field id="ragione_sociale" label="Ragione sociale" value={form.ragione_sociale} onChange={set("ragione_sociale")} testid="company-ragione-sociale-input" />
            <Field id="codice_fiscale" label="Codice fiscale" value={form.codice_fiscale} onChange={set("codice_fiscale")} testid="company-codice-fiscale-input" />
            <Field id="partita_iva" label="Partita IVA" value={form.partita_iva} onChange={set("partita_iva")} testid="company-partita-iva-input" />
            <Field id="forma_giuridica" label="Forma giuridica (es. S.R.L., S.P.A.)" value={form.forma_giuridica} onChange={set("forma_giuridica")} testid="company-forma-giuridica-input" />
            <Field id="settore" label="Settore" value={form.settore} onChange={set("settore")} testid="company-settore-input" />
            <Field id="codice_ateco" label="Codice ATECO" value={form.codice_ateco} onChange={set("codice_ateco")} testid="company-ateco-input" />
            <Field id="codice_nace" label="Codice NACE" value={form.codice_nace} onChange={set("codice_nace")} testid="company-nace-input" />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Sede legale e contatti</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field id="indirizzo" label="Indirizzo" value={form.indirizzo} onChange={set("indirizzo")} testid="company-indirizzo-input" />
            <Field id="citta" label="Città" value={form.citta} onChange={set("citta")} testid="company-citta-input" />
            <Field id="provincia" label="Provincia" value={form.provincia} onChange={set("provincia")} testid="company-provincia-input" />
            <Field id="cap" label="CAP" value={form.cap} onChange={set("cap")} testid="company-cap-input" />
            <Field id="nazione" label="Nazione" value={form.nazione} onChange={set("nazione")} testid="company-nazione-input" />
            <Field id="email" label="Email aziendale" value={form.email} onChange={set("email")} testid="company-email-input" />
            <Field id="sito_web" label="Sito web" value={form.sito_web} onChange={set("sito_web")} testid="company-sito-web-input" />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Dimensione impresa</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field id="numero_dipendenti" label="Numero dipendenti" type="number" value={form.numero_dipendenti} onChange={set("numero_dipendenti")} testid="company-dipendenti-input" />
            <Field id="fatturato" label="Fatturato annuo (€)" type="number" value={form.fatturato} onChange={set("fatturato")} testid="company-fatturato-input" />
            <Field id="totale_attivo" label="Totale attivo di bilancio (€)" type="number" value={form.totale_attivo} onChange={set("totale_attivo")} testid="company-attivo-input" />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Altre informazioni</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch id="gruppo" checked={form.appartenenza_gruppo}
                onCheckedChange={(v) => setForm({ ...form, appartenenza_gruppo: v })}
                disabled={!canEdit} data-testid="company-gruppo-switch" />
              <Label htmlFor="gruppo">L'impresa appartiene a un gruppo societario</Label>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="descrizione">Descrizione dell'attività</Label>
              <Textarea id="descrizione" rows={4} value={form.descrizione_attivita}
                onChange={set("descrizione_attivita")} disabled={!canEdit}
                data-testid="company-descrizione-input" />
            </div>
          </CardContent>
        </Card>

        {canEdit ? (
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-green-700 hover:bg-green-800" data-testid="company-save-button">
              <Save className="w-4 h-4 mr-2" aria-hidden="true" />
              {saving ? "Salvataggio…" : "Salva anagrafica"}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-600 bg-slate-100 border border-slate-200 rounded-md p-3" data-testid="readonly-notice">
            Il tuo ruolo ({role}) consente la sola visualizzazione dell'anagrafica.
          </p>
        )}
      </form>

      <SitesSection companyId={companyId} canEdit={canEdit} />
    </div>
  );
}
